import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import usePatchService from '@/hooks/queries/services-api/usePatchService';
import serviceNotificationSchema from '@/schemas/serviceNotification';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from 'lucide-react';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ServiceNotificationFormProps,
  ServicesNotificationFormValues
} from './ServiceNotificationForm.types';
const STATUS_KEYS = [
  'Pending',
  'Accepted',
  'Completed',
  'Absent',
  'Requested',
  'Proposed',
  'Rejected',
  'Cancelled'
] as const;

type StatusKey = (typeof STATUS_KEYS)[number];

const STATUS_LABELS: Record<StatusKey, { key: string }> = {
  Pending: {
    key: 'services.notifications.reservationStatus.labels.Pending'
  },
  Accepted: {
    key: 'services.notifications.reservationStatus.labels.Accepted'
  },
  Completed: {
    key: 'services.notifications.reservationStatus.labels.Completed'
  },
  Absent: {
    key: 'services.notifications.reservationStatus.labels.Absent'
  },
  Requested: {
    key: 'services.notifications.reservationStatus.labels.Requested'
  },
  Proposed: {
    key: 'services.notifications.reservationStatus.labels.Proposed'
  },
  Rejected: {
    key: 'services.notifications.reservationStatus.labels.Rejected'
  },
  Cancelled: {
    key: 'services.notifications.reservationStatus.labels.Cancelled'
  }
};

const ServiceNotificationForm: React.FC<ServiceNotificationFormProps> = ({
  service
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = usePatchService();

  const onSubmit = (data: any) => {
    mutate({
      payload: { notifications: data },
      serviceId: service.id
    });
  };

  const status = service.notifications?.reservationStatus;
  const mergedDefaultValues: ServicesNotificationFormValues = {
    reservationStatus: {
      Pending: status?.Pending ?? true,
      Accepted: status?.Accepted ?? true,
      Completed: status?.Completed ?? true,
      Absent: status?.Absent ?? true,
      Requested: status?.Requested ?? true,
      Proposed: status?.Proposed ?? true,
      Rejected: status?.Rejected ?? true,
      Cancelled: status?.Cancelled ?? true
    }
  };


  const form = useForm<ServicesNotificationFormValues>({
    resolver: zodResolver(serviceNotificationSchema),
    defaultValues: mergedDefaultValues
  });

  const hasChanged = !form.formState.isDirty;

  const getButtonContent = (): React.ReactNode => {
    if (isPending) {
      return <Loader className="w-4 h-4 animate-spin" />;
    }
    return t('form.update');
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('services.notifications.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('services.notifications.description')}
          </p>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Reservation Status Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">
                {t('services.notifications.reservationStatus.title')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('services.notifications.reservationStatus.subtitle')}
              </p>
            </div>

            <div className="rounded-lg border bg-card p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {STATUS_KEYS.map((statusKey) => (
                  <FormField
                    key={statusKey}
                    control={form.control}
                    name={`reservationStatus.${statusKey}` as const}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-md border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base font-medium">
                            {t(STATUS_LABELS[statusKey].key)}
                          </FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={!!field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex justify-end pt-4">
            <Button disabled={isPending || hasChanged} size="lg">
              {getButtonContent()}
            </Button>
          </div>
        </form>
      </div>
    </Form>
  );
};

export default ServiceNotificationForm;
